import {
  DirectedTripleWithFilters,
  DirectedTripleWithFiltersWithGraphIds,
  MetaModelTriple,
  PathCollapsingRule,
} from '@ardoq/api-types';
import { pick } from 'lodash';
import { isMetamodelWildcardTriple } from 'traverseOneLevel/defaultWildcardViewpoint';
import { getWorkspaceIdsAndTypeNamesFromTraversalPaths } from 'streams/perspectiveEditorData/getWorkspaceIdsAndTypeNamesFromTraversalPaths';
import {
  PathCollapsingRuleInternal,
  TraversalBuilderAsSavableAttributes,
  TraversalBuilderState,
} from '../types';
import { getPaths } from './getPaths';
import { getPathsWithDependencies } from './getPathsWithDependencies';
import { namedDirectedTripleToBackendFormat } from './namedDirectedTripleToBackendFormat';
import { viewpointBuilderFiltersOperations } from './viewpointBuilderFiltersOperations';

export const getTraversalsWithGraphIds = (state: TraversalBuilderState) =>
  getTraversals(state, null, true);

export function getTraversals(
  state: Pick<
    TraversalBuilderState,
    | 'selectedGraph'
    | 'graphNodes'
    | 'graphEdges'
    | 'startNode'
    | 'filters'
    | 'requiredNodeIds'
  >,
  targetNodeId: string | null,
  includeGraphIds: true
): DirectedTripleWithFiltersWithGraphIds[][];

export function getTraversals(
  {
    selectedGraph: { sourceMap, targetMap },
    graphNodes,
    graphEdges,
    startNode,
    filters,
    requiredNodeIds,
  }: Pick<
    TraversalBuilderState,
    | 'selectedGraph'
    | 'graphNodes'
    | 'graphEdges'
    | 'startNode'
    | 'filters'
    | 'requiredNodeIds'
  >,
  targetNodeId: string | null,
  includeGraphIds?: false
): DirectedTripleWithFilters[][];

export function getTraversals(
  {
    selectedGraph: { sourceMap, targetMap },
    graphNodes,
    graphEdges,
    startNode,
    filters,
    requiredNodeIds,
  }: Pick<
    TraversalBuilderState,
    | 'selectedGraph'
    | 'graphNodes'
    | 'graphEdges'
    | 'startNode'
    | 'filters'
    | 'requiredNodeIds'
  >,
  targetNodeId: string | null,
  includeGraphIds = false
): DirectedTripleWithFilters[][] | DirectedTripleWithFiltersWithGraphIds[][] {
  if (!startNode || graphEdges.size === 0) return [];

  return getPaths({
    graphNodeId: startNode.id,
    sourceMap,
    targetMap,
    graphNodes,
    graphEdges,
    path: [],
    visited: new Set([startNode.id]),
    filters,
    targetNodeId,
    requiredNodeIds,
    includeGraphIds,
  }).filter(path => path.length > 0);
}

export const getStartSetFilterId = (state: TraversalBuilderState) =>
  state.startNode
    ? (state.filters[state.startNode.id]?.localFilterId ?? null)
    : null;

const toSavableCollapsingRule = (
  internalRule: PathCollapsingRuleInternal
): PathCollapsingRule => ({
  ...pick(internalRule, [
    'path',
    'displayText',
    'referenceStyle',
    'isActive',
    'referenceTypeId',
  ]),
  // Only include when set, to keep the shape of a rule without recursion.
  ...(internalRule.collapseRecursively
    ? { collapseRecursively: internalRule.collapseRecursively }
    : {}),
});

export const getTraversalAsSavableAttributes = (
  state: TraversalBuilderState
): TraversalBuilderAsSavableAttributes => {
  const paths = getPathsWithDependencies(getTraversals(state, null));
  return {
    paths,
    filters: viewpointBuilderFiltersOperations.mapFiltersToBackendFormat(state),
    startType: state.startComponentType,
    startSetFilterId: getStartSetFilterId(state),
    pathMatching: state.pathMatching,
    // Only include when set, to avoid an explicit `undefined` key in the
    // savable attributes (keeps the shape stable for traversals without
    // expansion).
    ...(state.startSetExpansion
      ? {
          startSetExpansion: state.startSetExpansion,
          // `startType` is the `anyType` sentinel while the hierarchy node is in
          // place, so record the concrete type the expansion started from. For a
          // single-type hierarchy no node was swapped in and `startComponentType`
          // is already concrete.
          startSetExpansionFromType:
            state.startSetExpansionState?.originalStartComponentType ??
            state.startComponentType ??
            undefined,
        }
      : {}),
    pathCollapsingRules: state.pathCollapsing.rules.map(
      toSavableCollapsingRule
    ),
  };
};

export const getWorkspaceIdsAndComponentAndReferenceTypeNamesFromTraversals = (
  state: TraversalBuilderState
) => {
  const {
    componentNameAndWorkspacesIndex,
    referenceNameAndFieldsIndex,
    componentNameAndFieldsIndex,
    fieldMap,
    metaModelTriples,
  } = state;
  const paths = getTraversals(state, null);
  return getWorkspaceIdsAndTypeNamesFromTraversalPaths({
    paths,
    fieldMap,
    componentNameAndWorkspacesIndex,
    referenceNameAndFieldsIndex,
    componentNameAndFieldsIndex,
    triples: metaModelTriples,
  });
};

export const isNonWildcardTriple = (triple: MetaModelTriple) =>
  !isMetamodelWildcardTriple(triple);

export const isTripleWithSourceOrTargetType =
  (startComponentType: string) =>
  ({ sourceComponentType, targetComponentType }: MetaModelTriple) =>
    sourceComponentType === startComponentType ||
    targetComponentType === startComponentType;

export const toDirectedTriples =
  (startComponentType: string) =>
  ({
    sourceComponentType: sourceType,
    targetComponentType: targetType,
    referenceType,
  }: MetaModelTriple): DirectedTripleWithFilters[][] => {
    const isSymmetric = sourceType === targetType;
    if (isSymmetric) {
      return [
        [
          namedDirectedTripleToBackendFormat({
            sourceType,
            targetType,
            referenceType,
            direction: 'outgoing',
          }),
        ],
        [
          namedDirectedTripleToBackendFormat({
            sourceType,
            targetType,
            referenceType,
            direction: 'incoming',
          }),
        ],
      ];
    }

    const direction =
      sourceType === startComponentType ? 'outgoing' : 'incoming';
    return [
      [
        namedDirectedTripleToBackendFormat({
          sourceType,
          targetType,
          referenceType,
          direction,
        }),
      ],
    ];
  };
